<template>
  <div>
    <div class="body p-4">
      <div class="d-flex align-items-center flex-wrap mb-4">
        <h4 class="text-capitalize me-auto mb-0">
          <b>Notas Fiscais de Serviço Eletrônicas</b>
        </h4>
        <div class="d-flex align-items-center mx-3">
          <button
            type="button"
            class="btn btn-light btn-sm me-2"
            @click="stepPeriod(-1)"
          >
            <i class="fa fa-chevron-left"></i>
          </button>
          <span
            class="text-primary fw-bold text-capitalize period-label"
            @click="openPeriodPicker"
          >
            {{ periodLabel }}
          </span>
          <input ref="periodPickerInput" class="period-picker-input" type="text" />
          <button
            type="button"
            class="btn btn-light btn-sm ms-2"
            @click="stepPeriod(1)"
          >
            <i class="fa fa-chevron-right"></i>
          </button>
        </div>
        <button
          type="button"
          class="px-3 btn text-uppercase font-weight-light btn-primary text-white btn-shadow btn-sm"
          @click="$router.push('/cliente/nfse/novo')"
          v-prevent-click
        >
          <i class="fas fa-plus me-2"></i>Emitir NFS-e
        </button>
      </div>

      <div
        class="alert alert-warning text-start mb-4"
        v-if="nfseConfigured === false"
      >
        É preciso configurar a NFS-e antes de emitir.
      </div>

      <div class="row">
        <div class="col-lg-8 col-12 order-2 order-lg-1">
          <div class="card p-3 text-start">
            <div class="d-flex flex-wrap gap-2">
              <select
                class="form-select form-select-sm"
                style="max-width: 200px"
                v-model="filters.status"
                @change="applyFilters"
              >
                <option value="">Todos os status</option>
                <option
                  v-for="opt in statusOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
              <input
                type="text"
                class="form-control form-control-sm flex-grow-1"
                style="min-width: 200px"
                placeholder="Buscar cliente (nome ou documento)..."
                v-model="searchInput"
                @keyup.enter="applyFilters"
              />
              <button
                type="button"
                class="btn btn-primary btn-sm text-white"
                @click="applyFilters"
              >
                Filtrar
              </button>
            </div>
          </div>

          <div class="d-flex justify-content-center mt-4" v-if="loaderActive">
            <div class="spinner-border text-primary spinner-border" role="status">
              <span class="visually-hidden">Aguarde...</span>
            </div>
          </div>
          <div class="card p-3 mt-4 text-start" v-else-if="!nfses.length">
            Nenhuma NFS-e encontrada para o período e filtros selecionados.
          </div>
          <template v-else>
            <div class="card text-start mt-4 table-responsive">
              <table class="table align-middle mb-0">
                <thead>
                  <tr>
                    <th>N&ordm;</th>
                    <th>Emiss&atilde;o em</th>
                    <th>Cliente</th>
                    <th>Valor</th>
                    <th>Status</th>
                    <th class="text-end">A&ccedil;&otilde;es</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in nfses" :key="item.id">
                    <td>{{ displayNumero(item) }}</td>
                    <td>{{ formatDateTime(item.dh_emi || item.created_at) }}</td>
                    <td>{{ item.tomador_nome || "Não informado" }}</td>
                    <td>{{ formatCurrency(item.valor_servico) }}</td>
                    <td>
                      <span :class="['badge', 'rounded-pill', 'px-3', statusBadgeClass(item.status)]">
                        <i :class="['pe-1', statusIconClass(item.status)]"></i>
                        {{ statusLabel(item.status) }}
                      </span>
                    </td>
                    <td class="text-end">
                      <nfse-row-actions :item="item" @view-error="openErrorModal" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="mt-3" v-if="meta.last_page > 1">
              <base-pagination
                :pageCount="meta.last_page"
                :perPage="meta.per_page"
                :value="meta.current_page"
                size="md"
                align="center"
                @input="handlePageInput"
              />
            </div>
          </template>
        </div>

        <div class="col-lg-4 col-12 order-1 order-lg-2 mb-4 mb-lg-0">
          <div class="d-flex gap-2 mb-3 flex-wrap">
            <button
              type="button"
              class="btn btn-sm rounded-pill"
              :class="period === 'day' ? 'btn-primary text-white' : 'btn-light'"
              @click="setPeriod('day')"
            >
              Hoje
            </button>
            <button
              type="button"
              class="btn btn-sm rounded-pill"
              :class="period === 'week' ? 'btn-primary text-white' : 'btn-light'"
              @click="setPeriod('week')"
            >
              Esta semana
            </button>
            <button
              type="button"
              class="btn btn-sm rounded-pill"
              :class="period === 'month' ? 'btn-primary text-white' : 'btn-light'"
              @click="setPeriod('month')"
            >
              Este mês
            </button>
          </div>

          <div class="card p-3 text-start" v-if="summary.length">
            <div
              class="d-flex align-items-center mb-3"
              v-for="row in summary"
              :key="row.status"
            >
              <div class="summary-count me-3">{{ row.quantidade }}</div>
              <div>
                <div :class="['fw-bold', summaryTextClass(row.status)]">
                  {{ statusLabel(row.status) }}
                </div>
                <div class="text-muted">{{ formatCurrency(row.valor_total) }}</div>
              </div>
            </div>
          </div>
          <div class="card p-3 text-start text-muted" v-else-if="!loaderActive">
            Nenhum resumo disponível para o período.
          </div>
        </div>
      </div>
    </div>

    <modal :show="errorModal.open" @update="errorModal.open = $event">
      <template #header>
        <h5 class="mb-0">Erro na emissão</h5>
      </template>
      <p class="text-danger mb-0">{{ errorModal.message }}</p>
    </modal>
  </div>
</template>
<script>
import api from "@/services/api";
import { useToast } from "vue-toastification";
import BasePagination from "@/components/BasePagination.vue";
import NfseRowActions from "@/components/NfseRowActions.vue";
import flatpickr from "flatpickr";
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";
import "flatpickr/dist/flatpickr.min.css";
import "flatpickr/dist/plugins/monthSelect/style.css";

const toast = useToast();

const STATUS_META = {
  processing: {
    label: "Em processamento",
    badgeClass: "bg-warning text-dark",
    iconClass: "fas fa-clock",
    textClass: "text-warning",
  },
  submission_unknown: {
    label: "Envio não confirmado",
    badgeClass: "bg-warning text-dark",
    iconClass: "fa-solid fa-question",
    textClass: "text-warning",
  },
  authorized: {
    label: "Autorizada",
    badgeClass: "bg-success",
    iconClass: "fa-solid fa-circle-check",
    textClass: "text-success",
  },
  rejected: {
    label: "Rejeitada",
    badgeClass: "bg-danger",
    iconClass: "fa-solid fa-circle-xmark",
    textClass: "text-danger",
  },
  cancelled: {
    label: "Cancelada",
    badgeClass: "bg-secondary",
    iconClass: "fa-solid fa-ban",
    textClass: "text-secondary",
  },
  error: {
    label: "Erro",
    badgeClass: "bg-danger",
    iconClass: "fa-solid fa-triangle-exclamation",
    textClass: "text-danger",
  },
};

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toISODate(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function startOfWeek(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function endOfWeek(d) {
  const start = startOfWeek(d);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return end;
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

export default {
  name: "nfse-list",
  components: { BasePagination, NfseRowActions },
  data() {
    return {
      nfses: [],
      meta: { total: 0, per_page: 20, current_page: 1, last_page: 1 },
      summary: [],
      nfseConfigured: null,
      loaderActive: false,
      pollingIntervalId: null,
      loadRequestId: 0,
      pickerInstance: null,
      period: "month",
      referenceDate: new Date(),
      filters: { status: "", search: "" },
      searchInput: "",
      errorModal: { open: false, message: "" },
      statusOptions: Object.keys(STATUS_META).map((value) => ({
        value,
        label: STATUS_META[value].label,
      })),
    };
  },
  computed: {
    hasProcessing() {
      return this.nfses.some((n) => n.status === "processing");
    },
    dateRange() {
      const ref = this.referenceDate;
      if (this.period === "day") return { from: ref, to: ref };
      if (this.period === "week") return { from: startOfWeek(ref), to: endOfWeek(ref) };
      return { from: startOfMonth(ref), to: endOfMonth(ref) };
    },
    periodLabel() {
      const { from, to } = this.dateRange;
      if (this.period === "day") {
        return this.referenceDate.toLocaleDateString("pt-BR", {
          day: "2-digit",
          month: "long",
        });
      }
      if (this.period === "week") {
        const fromLabel = from.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
        const toLabel = to.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
        return `${fromLabel} – ${toLabel}`;
      }
      return this.referenceDate.toLocaleDateString("pt-BR", {
        month: "long",
        year: "numeric",
      });
    },
  },
  watch: {
    period() {
      this.initPeriodPicker();
    },
    referenceDate(newVal) {
      if (this.pickerInstance) this.pickerInstance.setDate(newVal, false);
    },
  },
  async mounted() {
    this.initPeriodPicker();
    await Promise.all([this.loadNfseConfig(), this.loadNfses()]);
  },
  beforeUnmount() {
    this.loadRequestId += 1;
    this.stopPolling();
    if (this.pickerInstance) {
      this.pickerInstance.destroy();
      this.pickerInstance = null;
    }
  },
  methods: {
    initPeriodPicker() {
      if (this.pickerInstance) {
        this.pickerInstance.destroy();
        this.pickerInstance = null;
      }
      const input = this.$refs.periodPickerInput;
      if (!input) return;

      const onChange = ([selected]) => {
        if (!selected) return;
        this.referenceDate = selected;
        this.meta.current_page = 1;
        this.loadNfses();
      };

      if (this.period === "month") {
        this.pickerInstance = flatpickr(input, {
          locale: Portuguese,
          clickOpens: false,
          appendTo: document.body,
          dateFormat: "m/Y",
          defaultDate: this.referenceDate,
          plugins: [new monthSelectPlugin({ shorthand: false })],
          onChange,
        });
      } else {
        this.pickerInstance = flatpickr(input, {
          locale: Portuguese,
          clickOpens: false,
          appendTo: document.body,
          dateFormat: "Y-m-d",
          defaultDate: this.referenceDate,
          onChange,
        });
      }
    },
    openPeriodPicker() {
      if (this.pickerInstance) this.pickerInstance.open();
    },
    async loadNfseConfig() {
      try {
        const { data } = await api.get("/company/nfse/config", {
          skipSessionExpiredHandler: true,
        });
        this.nfseConfigured = data !== null;
      } catch (e) {
        this.nfseConfigured = false;
        let message =
          "Ocorreu um erro ao carregar a configuração da NFSe. Recarregue a página e tente novamente.";
        if (e.response && e.response.data && e.response.data.message) {
          message = e.response.data.message;
        }
        toast.error(message);
      }
    },
    buildParams() {
      const { from, to } = this.dateRange;
      const params = {
        page: this.meta.current_page,
        per_page: this.meta.per_page,
      };
      if (from) params.date_from = toISODate(from);
      if (to) params.date_to = toISODate(to);
      if (this.filters.status) params.status = this.filters.status;
      if (this.filters.search) params.search = this.filters.search;
      return params;
    },
    async loadNfses(silent = false) {
      const requestId = ++this.loadRequestId;
      try {
        if (!silent) {
          this.stopPolling();
          this.loaderActive = true;
        }
        const { data } = await api.get("/company/nfse", {
          params: this.buildParams(),
          skipSessionExpiredHandler: true,
        });
        if (requestId !== this.loadRequestId) return;
        this.nfses = data.data || [];
        this.summary = data.summary || [];
        if (data.meta) {
          this.meta = { ...this.meta, ...data.meta };
        }
      } catch (e) {
        if (!silent && requestId === this.loadRequestId) {
          let message =
            "Ocorreu um erro ao carregar as NFSe. Recarregue a página e tente novamente.";
          if (e.response && e.response.data && e.response.data.message) {
            message = e.response.data.message;
          }
          toast.error(message);
        }
      } finally {
        if (requestId === this.loadRequestId) {
          if (!silent) this.loaderActive = false;
          this.updatePolling();
        }
      }
    },
    startPolling() {
      if (this.pollingIntervalId) return;
      this.pollingIntervalId = setInterval(() => {
        this.loadNfses(true);
      }, 15000);
    },
    stopPolling() {
      if (this.pollingIntervalId) {
        clearInterval(this.pollingIntervalId);
        this.pollingIntervalId = null;
      }
    },
    updatePolling() {
      if (this.hasProcessing) {
        this.startPolling();
      } else {
        this.stopPolling();
      }
    },
    setPeriod(period) {
      this.period = period;
      this.referenceDate = new Date();
      this.meta.current_page = 1;
      this.loadNfses();
    },
    stepPeriod(direction) {
      const d = new Date(this.referenceDate);
      if (this.period === "day") {
        d.setDate(d.getDate() + direction);
      } else if (this.period === "week") {
        d.setDate(d.getDate() + 7 * direction);
      } else {
        d.setDate(1);
        d.setMonth(d.getMonth() + direction);
      }
      this.referenceDate = d;
      this.meta.current_page = 1;
      this.loadNfses();
    },
    applyFilters() {
      this.filters.search = this.searchInput.trim();
      this.meta.current_page = 1;
      this.loadNfses();
    },
    handlePageInput(page) {
      this.meta.current_page = page;
      this.loadNfses();
    },
    openErrorModal(item) {
      this.errorModal.message = item.last_error || "Erro não informado.";
      this.errorModal.open = true;
    },
    displayNumero(item) {
      if (item.numero_nfse) return item.numero_nfse;
      if (item.n_dps) return item.n_dps;
      return `#${item.id}`;
    },
    formatDateTime(value) {
      if (!value) return "-";
      return new Date(value).toLocaleString("pt-BR");
    },
    formatCurrency(value) {
      if (value == null || value === "") return "-";
      return Number(value).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    statusLabel(status) {
      return (STATUS_META[status] && STATUS_META[status].label) || status || "-";
    },
    statusBadgeClass(status) {
      return (STATUS_META[status] && STATUS_META[status].badgeClass) || "bg-secondary";
    },
    statusIconClass(status) {
      return (STATUS_META[status] && STATUS_META[status].iconClass) || "fas fa-circle";
    },
    summaryTextClass(status) {
      return (STATUS_META[status] && STATUS_META[status].textClass) || "text-secondary";
    },
  },
};
</script>
<style scoped>
.summary-count {
  font-size: 1.75rem;
  font-weight: 700;
  color: #6c757d;
  min-width: 2.5rem;
  text-align: center;
}
.period-label {
  cursor: pointer;
}
.period-label:hover {
  text-decoration: underline;
}
.period-picker-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
  opacity: 0;
  pointer-events: none;
}
</style>
